import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  formatCorrectionFieldLabel,
  formatCorrectionFromTo,
} from "../hooks/useApproveBonds";

function getEmptyCorrectionsMessage(dataCorrectionsList, selectedType, bondNumberSearch) {
  if (dataCorrectionsList.length === 0) {
    return "No data corrections have been recorded.";
  }
  if (bondNumberSearch) {
    return `No ${selectedType || ""} corrections for this bond number search.`;
  }
  return `No ${selectedType || ""} corrections.`;
}

export default function ApproveBondsDataCorrectionsPanel({
  dataCorrectionsLoading,
  searchQuery,
  selectedType,
  dataCorrectionsList,
  setSelectedDataCorrection,
  setDataCorrectionDetailOpen,
  completingCorrectionId,
  getCorrectionId,
  updateCorrectionStatusToComplete,
}) {
  if (dataCorrectionsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
        <CircularProgress />
      </Box>
    );
  }

  const bondNumberSearch = (searchQuery || "").trim().toLowerCase();
  // Filter by selected bond type so Blanket view only shows Blanket corrections, etc.
  let filteredCorrections = dataCorrectionsList.filter((row) => (row.bond_type || "").toString().trim() === (selectedType || "").toString().trim());
  if (bondNumberSearch) {
    filteredCorrections = filteredCorrections.filter((row) => (row.bond_number || "").toString().toLowerCase().includes(bondNumberSearch));
  }
  if (filteredCorrections.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>No correction entries</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {getEmptyCorrectionsMessage(dataCorrectionsList, selectedType, bondNumberSearch)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", maxHeight: 360 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { backgroundColor: "#f0f0f0", color: "#111", fontWeight: 700, borderBottomColor: "rgba(0,0,0,0.12)" } }}>
                <TableCell>Bond Number</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Requester</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ "& tr:nth-of-type(odd)": { backgroundColor: "#fafafa" } }}>
              {filteredCorrections.map((row, idx) => (
                <TableRow key={`${selectedType}-${idx}`}>
                  <TableCell>{row.bond_number ?? "—"}</TableCell>
                  <TableCell>{formatCorrectionFieldLabel(row.changes?.field)}</TableCell>
                  <TableCell>{formatCorrectionFromTo(row.changes?.field, row.changes?.from)}</TableCell>
                  <TableCell>{formatCorrectionFromTo(row.changes?.field, row.changes?.to)}</TableCell>
                  <TableCell>{row.requester_info?.name ?? "—"} {row.requester_info?.role ? `(${row.requester_info.role})` : ""}</TableCell>
                  <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell>{row.status ?? "—"}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                      <Tooltip title="View details" arrow>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => {
                            setSelectedDataCorrection(row);
                            setDataCorrectionDetailOpen(true);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(row.status || "").toString().trim().toLowerCase() !== "complete" && (
                        <Tooltip title="Mark as Complete" arrow>
                          <span>
                            <IconButton
                              color="success"
                              size="small"
                              disabled={completingCorrectionId === getCorrectionId(row)}
                              onClick={() => updateCorrectionStatusToComplete(row)}
                            >
                              {completingCorrectionId === getCorrectionId(row) ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <CheckCircleIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

ApproveBondsDataCorrectionsPanel.propTypes = {
  dataCorrectionsLoading: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string.isRequired,
  selectedType: PropTypes.string.isRequired,
  dataCorrectionsList: PropTypes.array.isRequired,
  setSelectedDataCorrection: PropTypes.func.isRequired,
  setDataCorrectionDetailOpen: PropTypes.func.isRequired,
  completingCorrectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getCorrectionId: PropTypes.func.isRequired,
  updateCorrectionStatusToComplete: PropTypes.func.isRequired,
};